<template>
  <div class="bg-white rounded-[24px] p-5 flex flex-col h-full">
    <!-- header: avatar + name + handle -->
    <div class="flex items-center gap-3">
      <div
        class="size-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-white bg-[#f1f4f9] shadow-[0px_3px_12px_0px_rgba(49,118,255,0.14)]"
      >
        <img
          :src="channel.place.avatar || '/assets/img/preview_logo.svg'"
          class="w-full h-full object-cover"
          alt=""
        />
      </div>
      <div class="min-w-0">
        <div class="text-[18px] font-medium text-txprimary leading-[1.3] line-clamp-1">
          {{ channel.place.name }}
        </div>
        <div class="text-[12px] text-txopacity leading-4 truncate">
          {{ handle }}
        </div>
      </div>
    </div>

    <!-- description -->
    <p
      v-if="channel.place.description"
      class="text-[14px] text-txopacity leading-[1.5] mt-3 line-clamp-2"
    >
      {{ channel.place.description }}
    </p>

    <!-- categories -->
    <div v-if="categories.length" class="flex flex-wrap gap-1 mt-3">
      <span
        v-for="cat in categories.slice(0, 3)"
        :key="cat.id"
        class="bg-bgnew rounded-[8px] px-2.5 py-1 text-[12px] text-txprimary leading-[1.3]"
      >
        {{ cat.name }}
      </span>
    </div>

    <!-- stats -->
    <div class="flex items-stretch gap-2 mt-4">
      <div class="flex-1 min-w-0 bg-bgnew rounded-[16px] px-3 py-2.5">
        <div class="text-[12px] text-txopacity leading-4">Подписчики</div>
        <div class="text-[14px] font-medium text-txprimary leading-6">
          {{ formatNum(channel.place.subscribersStatistic?.count) }}
        </div>
      </div>
      <div class="flex-1 min-w-0 bg-bgnew rounded-[16px] px-3 py-2.5">
        <div class="text-[12px] text-txopacity leading-4">Охват</div>
        <div class="text-[14px] font-medium text-txprimary leading-6">
          {{ channel.place.er ?? 0 }}%
        </div>
      </div>
      <div class="flex-1 min-w-0 bg-bgnew rounded-[16px] px-3 py-2.5">
        <div class="text-[12px] text-txopacity leading-4">От</div>
        <div class="text-[14px] font-medium text-txprimary leading-6 whitespace-nowrap">
          {{ minPrice ? `${minPrice.toLocaleString()} ₽` : "—" }}
        </div>
      </div>
    </div>

    <!-- publisher comment -->
    <div
      v-if="channel.place?.publisherComment"
      class="mt-4 rounded-[16px] overflow-hidden relative pl-4 pr-3 py-2.5 bg-[#f9fafc]"
    >
      <div
        class="absolute left-0 top-0 h-full w-[3px]"
        :style="{ backgroundColor: color }"
      />
      <div class="text-[13px] font-medium leading-[1.4] mb-0.5" :style="{ color }">
        Комментарий автора
      </div>
      <p class="text-[12px] text-txopacity leading-[1.5] line-clamp-2">
        {{ channel.place.publisherComment }}
      </p>
    </div>

    <!-- actions: превью-кнопка по умолчанию, публичная страница подставляет свои -->
    <div class="mt-4 flex-1 flex flex-col justify-end gap-2">
      <slot name="actions">
        <ElTooltip content="Кнопка будет доступна рекламодателям" placement="top" effect="light">
          <button
            type="button"
            class="w-full h-10 rounded-[12px] flex items-center justify-center text-white text-[14px] cursor-help transition-opacity hover:opacity-90"
            :style="{ backgroundColor: color }"
          >
            Купить рекламу
          </button>
        </ElTooltip>
      </slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
  const props = withDefaults(defineProps<{ channel: any; color?: string }>(), {
    color: "#3176FF",
  });

  const socialLabel = (social?: string, url?: string): string => {
    const s = social || getSocialFromUrl(url || "");
    if (s === "vk") return "ВКонтакте";
    if (s === "max") return "MAX";
    return "Telegram";
  };

  const getSocialFromUrl = (url: string) => {
    const u = String(url || "").toLowerCase();
    if (u.includes("vk.com") || u.includes("vk.ru")) return "vk";
    if (u.includes("max.ru") || u.includes("my.mail.ru")) return "max";
    return "telegram";
  };

  const handle = computed(() => {
    const place = props.channel.place;
    const nick = String(place?.url || "")
      .replace(/^https?:\/\/(t\.me|vk\.com|vk\.ru)\//, "@")
      .replace(/\/$/, "");
    const label = socialLabel(place?.social, place?.url);
    return nick ? `${nick} · ${label}` : label;
  });

  const categories = computed(() => {
    const list = props.channel.place?.categories || [];
    const seen = new Set();
    return list.filter((c: any) => {
      if (seen.has(c.id)) return false;
      seen.add(c.id);
      return true;
    });
  });

  const minPrice = computed<number>(() => {
    const prices = props.channel.place?.prices || props.channel.place?.costs || [];
    const values = prices.map((p: any) => p.price).filter((v: any) => typeof v === "number" && v > 0);
    return values.length ? Math.min(...values) : 0;
  });

  const formatNum = (n?: number): string => {
    if (!n) return "0";
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(".0", "")}к`;
    return n.toLocaleString();
  };
</script>
